if (!customElements.get('spin-wheel')) {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const WHEEL_RADIUS = 88;

  const REPEAT_DAYS = {
    no_repeat: 365,
    every_1_hr: 1 / 24,
    every_6_hrs: 6 / 24,
    every_day: 1,
    every_3_days: 3,
    every_week: 7,
    every_2_weeks: 14,
    every_month: 30,
  };

  customElements.define(
    'spin-wheel',
    class SpinWheel extends DrawerComponent {
      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.config = JSON.parse(this.querySelector('[data-spin-config]').textContent);
        this.prizes = this.config.prizes;
        this.openedKey = 'casabella:spin-wheel:opened';
        this.prizeKey = 'casabella:spin-wheel:prize';

        this.wheel = this.querySelector('[data-wheel]');
        this.pointer = this.querySelector('.spin-wheel__pointer');
        this.signup = this.querySelector('[data-signup]');
        this.emailInput = this.signup?.querySelector('input[type="email"]');
        this.teaser = document.querySelector(`[data-spin-teaser][aria-controls="${this.id}"]`);

        this.rotation = 0;
        this.spinsLeft = this.config.maxSpins;
        this.emailSent = false;
        this.spinning = false;

        this.renderWheel();
        this.setStep('intro');

        const saved = Shopify.designMode ? null : FoxTheme.utils.getStorage(this.prizeKey);
        if (saved && saved.code) this.restoreWin(saved);

        const signal = this.abortController.signal;

        this.emailInput?.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            this.requestSpin();
          },
          { signal }
        );

        FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '[data-spin]',
          handler: (event) => {
            event.preventDefault();
            this.requestSpin();
          },
        });

        FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '[data-copy]',
          handler: (event, target) => {
            event.preventDefault();
            this.copyCode(target);
          },
        });

        if (Shopify.designMode) {
          this.closest('.shopify-section')?.addEventListener(
            'shopify:block:select',
            (event) => this.previewBlock(event.detail.blockId),
            { signal }
          );
        }

        FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      /* ----- Auto open ----- */

      handleOpen() {
        if (Shopify.designMode) return;

        const saved = FoxTheme.utils.getStorage(this.prizeKey);
        if (saved) {
          // The page left mid-spin (redirect, refresh): show the code that was won.
          if (!saved.revealed) this.showWhenFree();
          return;
        }

        if (this.config.trigger === 'manual') return;
        if (FoxTheme.utils.getStorage(this.openedKey)) return;

        const open = () => this.showWhenFree();

        switch (this.config.trigger) {
          case 'delay':
            setTimeout(open, this.config.delay * 1000);
            break;
          case 'scroll_down':
            this.onScrollPast(700, open);
            break;
          case 'exit_intent':
            if (FoxTheme.config.isTouch) {
              this.onScrollPast(document.documentElement.scrollHeight * 0.4, open);
            } else {
              const onLeave = (event) => {
                if (event.clientY > 0 || event.relatedTarget) return;
                document.removeEventListener('mouseout', onLeave);
                open();
              };
              document.addEventListener('mouseout', onLeave, { signal: this.abortController.signal });
            }
            break;
          default:
            open();
        }
      }

      onScrollPast(offset, callback) {
        const onScroll = () => {
          if (window.scrollY < offset) return;
          window.removeEventListener('scroll', onScroll);
          callback();
        };
        window.addEventListener('scroll', onScroll, { passive: true, signal: this.abortController.signal });
      }

      // Don't stack on top of the cart drawer, quick view, etc.
      showWhenFree() {
        if (document.body.classList.contains('modal-show')) {
          setTimeout(() => this.showWhenFree(), 4000);
          return;
        }
        this.show();
      }

      prepareToShow() {
        super.prepareToShow();
        if (Shopify.designMode) return;
        FoxTheme.utils.setStorage(this.openedKey, true, REPEAT_DAYS[this.config.repeat] ?? 3);
      }

      /* ----- Wheel drawing ----- */

      renderWheel() {
        const count = this.prizes.length;
        const slice = 360 / count;
        const palette = this.config.palette;
        const point = (deg, r) => {
          const rad = (deg * Math.PI) / 180;
          return [+(r * Math.sin(rad)).toFixed(3), +(-r * Math.cos(rad)).toFixed(3)];
        };
        const el = (tag, attrs = {}, parent = this.wheel) => {
          const node = document.createElementNS(SVG_NS, tag);
          Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
          parent.appendChild(node);
          return node;
        };

        this.wheel.replaceChildren();
        el('circle', { r: 100, class: 'spin-wheel__rim' });

        const bulbs = el('g', { class: 'spin-wheel__bulbs' });
        const bulbCount = Math.max(count * 2, 16);
        for (let i = 0; i < bulbCount; i++) {
          const [x, y] = point((360 / bulbCount) * i + slice / 2, 94.5);
          el('circle', { cx: x, cy: y, r: 2, class: 'spin-wheel__bulb' }, bulbs);
        }

        const fontSize = count > 8 ? 11 : count > 6 ? 13 : 15;

        this.prizes.forEach((prize, i) => {
          // Alternate the palette, and avoid the last slice matching the first.
          let tone = i % palette.length;
          if (i === count - 1 && tone === 0 && count > 1) tone = 1;
          const fill = prize.bg || palette[tone].bg;
          const text = prize.fg || palette[tone].fg;

          const start = slice * i;
          const [x1, y1] = point(start, WHEEL_RADIUS);
          const [x2, y2] = point(start + slice, WHEEL_RADIUS);
          const largeArc = slice > 180 ? 1 : 0;
          el('path', {
            d: `M0 0L${x1} ${y1}A${WHEEL_RADIUS} ${WHEEL_RADIUS} 0 ${largeArc} 1 ${x2} ${y2}Z`,
            fill,
            class: 'spin-wheel__slice',
          });

          const label = el('g', { transform: `rotate(${start + slice / 2})`, class: 'spin-wheel__label', fill: text });
          const main = el('text', { y: -60, 'font-size': fontSize, class: 'spin-wheel__label-main' }, label);
          main.textContent = prize.label;
          if (prize.sublabel) {
            const sub = el('text', { y: -60 + fontSize * 0.95, 'font-size': fontSize * 0.62 }, label);
            sub.textContent = prize.sublabel;
          }
        });

        for (let i = 0; i < count; i++) {
          const [x, y] = point(slice * i, WHEEL_RADIUS);
          el('line', { x1: 0, y1: 0, x2: x, y2: y, class: 'spin-wheel__divider' });
        }
        el('circle', { r: WHEEL_RADIUS, class: 'spin-wheel__ring' });
      }

      /* ----- Spinning ----- */

      requestSpin() {
        if (this.spinning || this.spinsLeft <= 0) return;

        // First spin needs a valid email.
        if (this.signup && !this.emailSent) {
          if (this.step !== 'intro') return;
          if (!this.emailInput.checkValidity()) {
            this.emailInput.reportValidity();
            this.emailInput.focus();
            return;
          }
          const index = this.spin();
          if (index !== undefined) this.subscribe(index);
          return;
        }
        this.spin();
      }

      // Posts the newsletter signup in the background; the page never navigates.
      subscribe(index) {
        this.emailSent = true;
        if (Shopify.designMode) return;

        const code = this.prizes[index].code;
        const body = new FormData();
        body.append('form_type', 'customer');
        body.append('utf8', '✓');
        body.append('contact[email]', this.emailInput.value.trim());
        body.append('contact[tags]', `newsletter,spin-wheel,${code ? `spin-wheel-${code}` : 'spin-wheel-no-prize'}`);

        fetch(this.signup.dataset.action, {
          method: 'POST',
          body,
          headers: { Accept: 'text/html' },
        }).catch(() => {});
      }

      pickPrize() {
        const weights = this.prizes.map((prize) => Math.max(0, Number(prize.chance) || 0));
        const total = weights.reduce((sum, weight) => sum + weight, 0);
        if (total === 0) return Math.floor(Math.random() * this.prizes.length);

        let roll = Math.random() * total;
        for (let i = 0; i < weights.length; i++) {
          roll -= weights[i];
          if (roll < 0) return i;
        }
        return weights.length - 1;
      }

      spin() {
        if (this.spinning || this.spinsLeft <= 0) return;

        this.spinning = true;
        this.spinsLeft -= 1;
        this.classList.add('is-spinning');
        this.querySelectorAll('[data-spin]').forEach((btn) => (btn.disabled = true));

        const index = this.pickPrize();
        const prize = this.prizes[index];
        const persist = prize.code && !Shopify.designMode;

        // Persist straight away so a refresh mid-spin can't buy another try,
        // and so the code is shown on the next page if this one goes away.
        if (persist) {
          FoxTheme.utils.setStorage(
            this.prizeKey,
            { ...this.serializePrize(prize), revealed: false },
            this.config.rememberDays
          );
        }

        const reduced = FoxTheme.config.motionReduced;
        const duration = reduced ? 1200 : 6200;
        const from = this.rotation;
        const to = this.targetRotation(index, reduced ? 1 : 6, true);

        const animation = this.wheel.animate(
          [{ transform: `rotate(${from}deg)` }, { transform: `rotate(${to}deg)` }],
          { duration, easing: 'cubic-bezier(0.12, 0.62, 0.08, 1)', fill: 'forwards' }
        );
        if (!reduced) this.tickPointer(animation);

        // Fallback timer in case the animation clock stalls (e.g. throttled tab).
        const settled = new Promise((resolve) => {
          animation.finished.then(resolve, resolve);
          setTimeout(resolve, duration + 600);
        });

        settled.then(() => {
          this.wheel.style.transform = `rotate(${to}deg)`;
          animation.cancel();
          this.rotation = to;
          this.spinning = false;
          this.classList.remove('is-spinning');
          this.querySelectorAll('[data-spin]').forEach((btn) => (btn.disabled = false));

          if (prize.code) {
            if (persist) {
              FoxTheme.utils.setStorage(
                this.prizeKey,
                { ...this.serializePrize(prize), revealed: true },
                this.config.rememberDays
              );
            }
            this.showWin(prize);
            if (!reduced) this.celebrate();
          } else {
            this.showMiss();
          }
        });

        return index;
      }

      // Rotation (deg, clockwise) that puts the middle of slice `index` under the top pointer.
      targetRotation(index, turns, jitter = false) {
        const slice = 360 / this.prizes.length;
        const offset = jitter ? (Math.random() - 0.5) * slice * 0.6 : 0;
        const landing = 360 - (slice * index + slice / 2) + offset;
        const current = ((this.rotation % 360) + 360) % 360;
        const delta = (((landing - current) % 360) + 360) % 360;
        return this.rotation + turns * 360 + delta;
      }

      tickPointer(animation) {
        const slice = 360 / this.prizes.length;
        let lastSlice = null;

        const frame = () => {
          if (animation.playState !== 'running') return;
          const matrix = new DOMMatrixReadOnly(getComputedStyle(this.wheel).transform);
          const angle = ((Math.atan2(matrix.b, matrix.a) * 180) / Math.PI + 360) % 360;
          const current = Math.floor(angle / slice);
          if (lastSlice !== null && current !== lastSlice) {
            this.pointer.animate(
              [{ transform: 'rotate(0deg)' }, { transform: 'rotate(-16deg)' }, { transform: 'rotate(0deg)' }],
              { duration: 140, easing: 'ease-out' }
            );
          }
          lastSlice = current;
          requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      }

      /* ----- Results ----- */

      setStep(step) {
        this.step = step;
        this.dataset.step = step;
        this.querySelectorAll('[data-step-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.stepPanel !== step;
        });
      }

      serializePrize(prize) {
        return { id: prize.id, code: prize.code, title: prize.title || `${prize.label} ${prize.sublabel}`.trim() };
      }

      showWin(prize) {
        const { code, title } = this.serializePrize(prize);
        this.querySelector('[data-prize-title]').textContent = title;
        this.querySelector('[data-prize-code]').textContent = code;

        const redirect = this.config.redirect || window.location.pathname + window.location.search;
        this.querySelector('[data-apply]').href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(
          redirect
        )}`;

        this.spinsLeft = 0;
        this.setStep('win');
        this.querySelector('[data-step-panel="win"] .spin-wheel__cta')?.focus({ preventScroll: true });

        if (this.teaser && this.teaser.dataset.wonText) {
          this.teaser.querySelector('[data-teaser-label]').textContent = this.teaser.dataset.wonText;
        }
      }

      restoreWin(saved) {
        const index = this.prizes.findIndex((prize) => prize.code === saved.code);
        if (index >= 0) {
          this.rotation = this.targetRotation(index, 0);
          this.wheel.style.transform = `rotate(${this.rotation}deg)`;
        }
        this.showWin(saved);
      }

      showMiss() {
        const canRetry = this.spinsLeft > 0;
        this.setStep('miss');
        this.querySelectorAll('[data-miss-retry]').forEach((el) => (el.hidden = !canRetry));
        this.querySelectorAll('[data-miss-final]').forEach((el) => (el.hidden = canRetry));
        this.querySelector('[data-step-panel="miss"] .spin-wheel__cta:not([hidden])')?.focus({ preventScroll: true });
      }

      async copyCode(button) {
        const code = this.querySelector('[data-prize-code]').textContent;
        try {
          await navigator.clipboard.writeText(code);
        } catch (error) {
          const range = document.createRange();
          range.selectNodeContents(this.querySelector('[data-prize-code]'));
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
          document.execCommand('copy');
        }

        button.dataset.label = button.dataset.label || button.textContent.trim();
        button.textContent = button.dataset.copiedText;
        button.classList.add('is-copied');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          button.textContent = button.dataset.label;
          button.classList.remove('is-copied');
        }, 2200);
      }

      // Full-screen canvas confetti: a burst from the wheel, two side cannons, then a gentle rain.
      celebrate() {
        this.confetti?.stop();

        const canvas = document.createElement('canvas');
        canvas.className = 'spin-wheel-confetti';
        canvas.setAttribute('aria-hidden', 'true');
        document.body.appendChild(canvas);

        const ctx = canvas.getContext('2d');
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const resize = () => {
          canvas.width = window.innerWidth * dpr;
          canvas.height = window.innerHeight * dpr;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener('resize', resize);

        const styles = getComputedStyle(this);
        const accent = styles.getPropertyValue('--sw-accent').trim() || '#c9a77c';
        const rim = styles.getPropertyValue('--sw-rim').trim() || '#272424';
        // Weighted toward tones that read on both the cream panel and the blush stage.
        const colors = [accent, accent, '#b08d57', '#e3c99a', rim, '#d9a19b', '#c9837c', '#ead0ca', '#ffffff'];

        const rand = (min, max) => min + Math.random() * (max - min);
        const density = window.innerWidth < 768 ? 0.6 : 1;
        const pieces = [];

        const add = (x, y, angle, speed) => {
          const ribbon = Math.random() < 0.28;
          pieces.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            w: ribbon ? rand(4, 6) : rand(8, 13),
            h: ribbon ? rand(18, 28) : rand(10, 16),
            round: !ribbon && Math.random() < 0.18,
            color: colors[Math.floor(Math.random() * colors.length)],
            rot: rand(0, Math.PI * 2),
            spin: rand(-5, 5),
            flip: rand(0, Math.PI * 2),
            flipSpeed: rand(4, 10),
            sway: rand(0, Math.PI * 2),
            swaySpeed: rand(1.5, 3.5),
            drag: rand(2.4, 3.4),
          });
        };
        const burst = (count, spawn) => {
          for (let i = 0; i < Math.round(count * density); i++) spawn();
        };

        const box = this.wheel.getBoundingClientRect();
        const cx = box.left + box.width / 2;
        const cy = box.top + box.height / 2;

        burst(140, () => add(cx, cy, rand(-Math.PI * 0.95, -Math.PI * 0.05), rand(500, 1300)));

        const GRAVITY = 620;
        const RAIN_UNTIL = 3400;
        const FADE_FROM = 5600;
        const END = 6800;
        let cannonsFired = false;
        let rainDebt = 0;
        let elapsed = 0;
        let last = performance.now();
        let frame;

        const stop = () => {
          cancelAnimationFrame(frame);
          window.removeEventListener('resize', resize);
          canvas.remove();
          if (this.confetti === controller) this.confetti = null;
        };
        const controller = { stop };
        this.confetti = controller;

        const tick = (now) => {
          const dt = Math.min((now - last) / 1000, 0.05);
          last = now;
          elapsed += dt * 1000;
          const width = window.innerWidth;
          const height = window.innerHeight;

          if (!cannonsFired && elapsed > 350) {
            cannonsFired = true;
            burst(80, () => add(-10, height * 0.9, rand(-1.45, -0.7), rand(900, 1900)));
            burst(80, () => add(width + 10, height * 0.9, -Math.PI + rand(0.7, 1.45), rand(900, 1900)));
          }
          if (elapsed > 600 && elapsed < RAIN_UNTIL) {
            rainDebt += 70 * density * dt;
            for (; rainDebt >= 1; rainDebt--) add(rand(0, width), -20, Math.PI / 2 + rand(-0.3, 0.3), rand(40, 160));
          }

          ctx.clearRect(0, 0, width, height);
          ctx.globalAlpha = elapsed > FADE_FROM ? Math.max(0, 1 - (elapsed - FADE_FROM) / (END - FADE_FROM)) : 1;

          for (let i = pieces.length - 1; i >= 0; i--) {
            const p = pieces[i];
            const damping = Math.exp(-p.drag * dt);
            p.vx *= damping;
            p.vy = p.vy * damping + GRAVITY * dt;
            p.sway += p.swaySpeed * dt;
            p.x += (p.vx + Math.sin(p.sway) * 45) * dt;
            p.y += p.vy * dt;
            p.rot += p.spin * dt;
            p.flip += p.flipSpeed * dt;

            if (p.y > height + 40) {
              pieces.splice(i, 1);
              continue;
            }

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rot);
            ctx.scale(1, Math.cos(p.flip));
            ctx.fillStyle = p.color;
            if (p.round) {
              ctx.beginPath();
              ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
              ctx.fill();
            } else {
              ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
            }
            ctx.restore();
          }

          if (elapsed >= END || (elapsed > RAIN_UNTIL && pieces.length === 0)) {
            stop();
            return;
          }
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      }

      /* ----- Theme editor ----- */

      previewBlock(blockId) {
        const index = this.prizes.findIndex((prize) => prize.id === blockId);
        if (index < 0 || this.spinning) return;
        this.show(null, false);
        this.rotation = this.targetRotation(index, 0);
        this.wheel.style.transform = `rotate(${this.rotation}deg)`;
      }
    }
  );
}
